import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import ChunkedScrollManager from './chunked-scroll.js';

/**
 * A custom element that renders a paginated results list with infinite scroll
 */
export default class ResultsList extends PaginatedList {
  /** @type {Map<HTMLElement, Record<string, string>>} */
  #originalStyles = new Map();
  #isTransitioning = false;

  /** @type {ChunkedScrollManager | null} */
  #chunkedScroll = null;

  /** @type {MutationObserver | null} */
  #productLinkObserver = null;

  /** @type {(event: MouseEvent) => void} | null} */
  #collectionProductClickHandler = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    // Restore layout preference from storage
    this.#restoreLayoutFromStorage();

    this.#initializeCollectionProductLinks();

    // Initialize chunked scroll
    this.#initializeChunkedScroll();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    // Cleanup any stored styles to prevent memory leaks
    this.#cleanupLayoutStyles();
    this.#productLinkObserver?.disconnect();
    this.#productLinkObserver = null;
    if (this.#collectionProductClickHandler) {
      this.removeEventListener('click', this.#collectionProductClickHandler, true);
      this.#collectionProductClickHandler = null;
    }
    // Cleanup chunked scroll
    if (this.#chunkedScroll) {
      this.#chunkedScroll.destroy();
      this.#chunkedScroll = null;
    }
  }

  /**
   * @returns {string}
   */
  #getCollectionHandle() {
    return this.getAttribute('data-collection-handle') || '';
  }

  /**
   * @param {string} handle
   */
  #storePlpBreadcrumbContext(handle) {
    const title = this.getAttribute('data-collection-title') || '';
    try {
      sessionStorage.setItem('cm-plp-breadcrumb', JSON.stringify({ handle, title }));
    } catch {
      // ignore quota / privacy mode
    }
  }

  /**
   * Rewrites /products/* links to include collection context for PDP breadcrumbs.
   * @param {ParentNode} root
   */
  #applyCollectionContextToProductLinks(root = this) {
    const collectionHandle = this.#getCollectionHandle();
    if (!collectionHandle) return;

    const prefix = `/collections/${collectionHandle}`;
    root.querySelectorAll('a[href^="/products/"]').forEach((anchor) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const url = new URL(anchor.href, window.location.origin);
      if (!url.pathname.startsWith('/products/')) return;
      url.pathname = `${prefix}${url.pathname}`;
      anchor.href = `${url.pathname}${url.search}${url.hash}`;
    });
  }

  /**
   * Keeps product URLs collection-scoped when Depict or other scripts reset hrefs.
   */
  #initializeCollectionProductLinks() {
    if (!this.#getCollectionHandle()) return;

    this.#applyCollectionContextToProductLinks();

    const grid = this.querySelector('[ref="grid"]');
    if (grid) {
      this.#productLinkObserver = new MutationObserver(() => {
        this.#applyCollectionContextToProductLinks();
      });
      this.#productLinkObserver.observe(grid, { childList: true, subtree: true });
    }

    this.#collectionProductClickHandler = (event) => {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest('a[href*="/products/"]');
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const collectionHandle = this.#getCollectionHandle();
      if (!collectionHandle) return;

      const url = new URL(anchor.href, window.location.origin);
      if (!url.pathname.startsWith('/products/')) return;

      const shouldOpenInNewTab =
        event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;
      if (shouldOpenInNewTab) return;

      url.pathname = `/collections/${collectionHandle}${url.pathname}`;
      this.#storePlpBreadcrumbContext(collectionHandle);
      event.preventDefault();
      window.location.assign(url.href);
    };

    this.addEventListener('click', this.#collectionProductClickHandler, true);
  }

  /**
   * Restore layout preference from sessionStorage
   */
  #restoreLayoutFromStorage() {
    const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
    const storedLayout = sessionStorage.getItem(`product-grid-view-${viewport}`);

    if (storedLayout) {
      this.#setLayout(storedLayout);
      this.#updateImageSizes(storedLayout);
    }
  }

  /**
   * Cleanup layout styles to prevent memory leaks
   */
  #cleanupLayoutStyles() {
    this.#originalStyles.clear();
    const galleries = this.querySelectorAll('.card-gallery');
    galleries.forEach(gallery => {
      gallery.removeAttribute('data-aspect-ratio-applied');
    });
  }

  /**
   * Initialize chunked infinite scroll
   */
  #initializeChunkedScroll() {
    // Get configuration from DOM
    const resultsListElement = this.querySelector('#ResultsList');
    if (!resultsListElement) {
      console.warn('[results-list] ResultsList element not found, chunked scroll not initialized');
      return;
    }

    const totalPages = parseInt(resultsListElement.dataset.totalPages || '1', 10);
    const currentPage = parseInt(resultsListElement.dataset.currentPage || '1', 10);
    const sectionId = this.getAttribute('section-id');
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const collectionsIndex = pathParts.indexOf('collections');
    const collectionHandle = collectionsIndex >= 0 ? pathParts[collectionsIndex + 1] || '' : '';

    // Create chunked scroll manager with configuration
    this.#chunkedScroll = new ChunkedScrollManager({
      totalPages,
      sectionId,
      collectionHandle,
      parentElement: this,
    });

    // Set refs from DOM
    this.#chunkedScroll.refs = {
      grid: this.querySelector('[ref="grid"]'),
      topSentinel: this.querySelector('[ref="topSentinel"]'),
      bottomSentinel: this.querySelector('[ref="bottomSentinel"]'),
      loadingIndicator: this.querySelector('[ref="loadingIndicator"]'),
      topLoadingIndicator: this.querySelector('[ref="topLoadingIndicator"]'),
      endIndicator: this.querySelector('[ref="endIndicator"]'),
      loadMoreContainer: this.querySelector('[ref="loadMoreContainer"]'),
      loadMoreButton: this.querySelector('[ref="loadMoreButton"]'),
    };

    // Set aspect ratio processor callback
    this.#chunkedScroll.setAspectRatioProcessor(() => {
      // Use parent class method for aspect ratio processing
      this.processNewElements();
      this.#applyCollectionContextToProductLinks();
    });

    // Initialize the chunked scroll manager
    this.#chunkedScroll.init();
  }

/**
 * Updates the layout. If no layout provided, cycles to next layout.
 *
 * @param {Event} event
 */
updateLayout(event) {
  // Prevent clicks during transitions
  if (this.#isTransitioning) {
    console.warn('[results-list] Layout change already in progress');
    return;
  }

  // First try to get explicit layout from event
  let layout =
    event?.detail?.layout ??
    (event.target instanceof HTMLInputElement
      ? event.target.value
      : event?.target?.dataset?.layout);

  // If event came from button, cycle to next layout
  if (event.target instanceof HTMLButtonElement) {
    const { grid } = this.refs;

    // Defensive check with error feedback
    if (!grid) {
      console.error('[results-list] Grid reference not found. Cannot update layout.');
      return;
    }

    const currentLayout = grid.getAttribute('product-grid-view') || 'default';
    const isMobile = window.innerWidth <= 768;

    // Define layout sequences (same as custom.js)
    const mobileLayouts = ["zoom-in", "zoom-out", "default"];
    const desktopLayouts = ["zoom-out", "zoom-in", "default"];
    const layouts = isMobile ? mobileLayouts : desktopLayouts;

    // Find current index and cycle to next
    const currentIndex = layouts.indexOf(currentLayout);
    const nextIndex = (currentIndex + 1) % layouts.length;
    layout = layouts[nextIndex];

    // Update button dataset for consistency
    event.target.dataset.layout = layout;
    event.target.dataset.currentIndex = nextIndex.toString();
  }

  if (!layout) return;

  // Set transitioning state
  this.#isTransitioning = true;

  // Disable button during transition
  const button = event.target instanceof HTMLButtonElement ? event.target : null;
  if (button) {
    button.disabled = true;
    button.classList.add('button--transitioning');
  }

  this.#animateLayoutChange(layout, button);
}



  /**
   * Sets the layout.
   *
   * @param {string} value
   * @param {HTMLElement} button
   */
  #animateLayoutChange = async (value, button = null) => {
    const { grid } = this.refs;

    if (!grid) {
      console.error('[results-list] Grid reference not found in animateLayoutChange');
      this.#isTransitioning = false;
      if (button) {
        button.disabled = false;
        button.classList.remove('button--transitioning');
      }
      return;
    }

    //await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    this.#setLayout(value);

    // Wait for layout to settle before scrolling and re-enabling button
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Scroll to top after layout reflow completes
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

        // Re-enable button after layout settles
        this.#isTransitioning = false;
        if (button) {
          button.disabled = false;
          button.classList.remove('button--transitioning');
        }
      });
    });

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;

    // Add transitioning class to grid for visual feedback
    grid.classList.add('transitioning');

    // Store previous value to detect changes
    const previousValue = grid.getAttribute('product-grid-view');
    grid.setAttribute('product-grid-view', value);

    // Cancel unnecessary image loads based on new layout
    this.#cancelUnnecessaryImageLoads(value);

    // REMOVED: Image size updates were causing 10+ second delays and crashes
    // The sizes attribute is only useful for initial load - images are already loaded
    // Browsers will use cached images and scale them appropriately via CSS
    // this.#updateImageSizes(value);

    // REMOVED: Aspect ratio reprocessing was causing layout thrashing
    // CSS handles the layout, no need to modify inline styles
    // if (value === 'zoom-out') this.#reprocessAspectRatiosForZoomOut();
    // if (isMobile && value === 'zoom-in') this.#reprocessAspectRatiosForZoomIn();

    // Remove transitioning class after brief delay
    setTimeout(() => {
      grid.classList.remove('transitioning');

      // Sync pagination markers with actual scroll position after layout change
      const galleries = grid.querySelectorAll('.card-gallery');
      galleries.forEach(gallery => {
        gallery.dispatchEvent(new CustomEvent('pagination-sync'));
      });
    }, 300);
  }

  /**
   * Cancels or restores image loads based on the new layout.
   * - Layouts with hidden carousel slides: cancel pending loads
   * - Layouts with visible carousel: restore cancelled images
   *
   * @param {string} layout
   */
  #cancelUnnecessaryImageLoads(layout) {
    const { grid } = this.refs;
    if (!grid) return;

    // In zoom-in (1-column) or zoom-out (tiles) mode, carousel slides are hidden
    if (layout === 'zoom-in' || layout === 'zoom-out') {
      // Find all carousel slides that are NOT the first slide
      const hiddenSlides = grid.querySelectorAll('slideshow-slide:nth-child(n+2)');
      hiddenSlides.forEach(slide => {
        const img = slide.querySelector('img');
        if (img && !img.complete && img.src) {
          // Store original src so we can restore it later
          img.dataset.cancelledSrc = img.src;
          // Remove src to cancel pending request
          img.removeAttribute('src');
        }
      });
    }

    // In default (2-column) mode, carousel is visible - restore any cancelled images
    if (layout === 'default') {
      const allSlides = grid.querySelectorAll('slideshow-slide:nth-child(n+2)');
      allSlides.forEach(slide => {
        const img = slide.querySelector('img');
        if (img && img.dataset.cancelledSrc && !img.src) {
          // Restore the original src to trigger load
          img.src = img.dataset.cancelledSrc;
          delete img.dataset.cancelledSrc;
        }
      });
    }
  }

  /**
   * Updates image sizes attribute based on current layout and viewport.
   * This ensures browsers download appropriately sized images for each layout.
   *
   * @param {string} layout
   */
  #updateImageSizes(layout) {
    const isMobile = !mediaQueryLarge.matches;
    let sizesValue;

    if (isMobile) {
      switch (layout) {
        case 'zoom-out':
          sizesValue = '25vw'; // 4 columns on mobile
          break;
        case 'two-column':
          sizesValue = '50vw'; // 2 columns
          break;
        default:
          sizesValue = '100vw'; // 1 column (full width)
      }
    } else {
      switch (layout) {
        case 'zoom-in':
          sizesValue = '(min-width: 1024px) 100vw, (min-width: 750px) 50vw, 50vw'; // 2 columns
          break;
        case 'zoom-out':
          sizesValue = '(min-width: 1024px) 33vw, (min-width: 750px) 50vw, 50vw'; // 6 columns
          break;
        default:
          sizesValue = '(min-width: 1024px) 50vw, (min-width: 750px) 50vw, 50vw'; // 4 columns
      }
    }

    // Only update images in or near viewport to prevent memory spike on mobile
    // When switching to larger images (e.g., zoom-in), updating ALL images at once
    // can cause 144 images to fetch larger versions simultaneously, crashing mobile browsers
    const images = this.querySelectorAll('.product-media__image');
    const viewportHeight = window.innerHeight;

    images.forEach(img => {
      const rect = img.getBoundingClientRect();
      // Update if in viewport or within 500px buffer (roughly 2-3 products)
      if (rect.top < viewportHeight + 500 && rect.bottom > -500) {
        img.setAttribute('sizes', sizesValue);
      }
    });
  }

  /**
   * Reprocess aspect ratios when switching to zoom-out mode
   * This fixes spacing issues when toggling view modes
   */
  #reprocessAspectRatiosForZoomOut() {
    // In zoom-out mode, let images maintain their natural proportions
    // Remove any forced aspect ratios and let CSS handle the layout
    const galleries = this.querySelectorAll('.card-gallery');
    galleries.forEach(gallery => {
      // Remove any inline aspect ratio styles
      gallery.style.removeProperty('--gallery-aspect-ratio');

      const mediaContainers = gallery.querySelectorAll('.product-media-container');
      mediaContainers.forEach(container => {
        container.style.removeProperty('aspect-ratio');
      });

      // Mark as processed to prevent further automatic processing
      gallery.setAttribute('data-aspect-ratio-applied', 'zoom-out');
    });
  }

  /**
   * Reprocess aspect ratios when switching to zoom-in (single column) mode on mobile
   * Cleans up forced aspect ratios to prevent memory buildup
   */
  #reprocessAspectRatiosForZoomIn() {
    const galleries = this.querySelectorAll('.card-gallery');
    galleries.forEach(gallery => {
      // Remove any inline aspect ratio styles
      gallery.style.removeProperty('--gallery-aspect-ratio');

      const mediaContainers = gallery.querySelectorAll('.product-media-container');
      mediaContainers.forEach(container => {
        container.style.removeProperty('aspect-ratio');
      });

      // Mark as processed for zoom-in
      gallery.setAttribute('data-aspect-ratio-applied', 'zoom-in');
    });
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
